import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HistoryTools from "../HistoryTools";
import EditModal from "../EditModal";
import AttachModal from "../AttachModel";
import HistoryModel from "../HistoryModel";
import CommentsModel from "../CommentsModel";
import UserModel from "../UserModel";
import OpenModel from "../OpenModel";
import "../../styles/ReportSummary.css";

const DATE_TYPE_OPTIONS = [
  { value: "UPLOADED_AT", label: "Uploaded Date" },
  { value: "STUDY_DATE_TIME", label: "Study Date" },
  { value: "SIGNED_OFF_DATE", label: "Signed Off Date" }
];

export default function ReportSummary({ studyData, rowsPerPage, setRowsPerPage, currentPage, setCurrentPage }) {
  const today = new Date();
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(today.getMonth() - 2);
  
  const [startTime, setStartTime] = useState(twoMonthsAgo.toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState(today.toISOString().split('T')[0]);
  const [dateType, setDateType] = useState("SIGNED_OFF_DATE"); 
  
  const [rsColFilters, setRsColFilters] = useState({
    id: "", patient: "", study: "", referral: "", status: "",
    center: "", assigned: "",
    studyDateFrom: "", studyDateTo: "", uploadedDate: "", signedOffDate: "",
  });

  const [editPatient, setEditPatient] = useState(null);
  const [attachmentPatient, setAttachmentPatient] = useState(null);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [commentsPatient, setCommentsPatient] = useState(null);
  const [userPatient, setUserPatient] = useState(null);
  const [openPatient, setOpenPatient] = useState(null);
  const [assignModal,setAssignModal] = useState(false);
  const [selectedPatient,setSelectedPatient] = useState(null);
  const navigate = useNavigate();

  const updateRsFilter = (key, value) => {
    setRsColFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearDate = () => {
    setStartTime("");
    setEndTime("");
    setCurrentPage(1);
  };

  const getDateValue = (row, type) => {
    switch(type) {
      case "UPLOADED_AT":
        return row.uploaded?.split(' ')[0] || row.uploaded;
      case "STUDY_DATE_TIME":
        return row.studyDate;
      case "SIGNED_OFF_DATE":
        return row.signedOff?.split(' ')[0] || row.signedOff;
      default:
        return row.signedOff?.split(' ')[0] || row.signedOff;
    }
  };

  const rsFilteredData = useMemo(() => {
    if (!studyData || studyData.length === 0) return [];
    
    return studyData.filter((row) => {
      if (row.status !== "SIGNED OFF") return false;
      const rowDate = getDateValue(row, dateType);
      let dateMatch = true;
      if (startTime !== "" && endTime !== "") {
        dateMatch = rowDate >= startTime && rowDate <= endTime;
      } else if (startTime !== "") {
        dateMatch = rowDate >= startTime;
      } else if (endTime !== "") {
        dateMatch = rowDate <= endTime;
      }

      const f = rsColFilters;
      const colMatch =
        (f.id === "" || row.id?.toString().includes(f.id)) &&
        (f.patient === "" || row.patient?.toLowerCase().includes(f.patient.toLowerCase())) &&
        (f.study === "" || row.study?.toLowerCase().includes(f.study.toLowerCase())) &&
        (f.referral === "" || row.referral?.toLowerCase().includes(f.referral.toLowerCase())) &&
        (f.status === "" || row.status?.toLowerCase().includes(f.status.toLowerCase())) &&
        (f.center === "" || row.center?.toLowerCase().includes(f.center.toLowerCase())) &&
        (f.assigned === "" || row.assigned?.toLowerCase().includes(f.assigned.toLowerCase())) &&
        (f.studyDateFrom === "" || row.studyDate >= f.studyDateFrom) &&
        (f.studyDateTo === "" || row.studyDate <= f.studyDateTo) &&
        (f.uploadedDate === "" || row.uploaded >= f.uploadedDate) &&
        (f.signedOffDate === "" || row.signedOff >= f.signedOffDate);
      
      return dateMatch && colMatch;
    });
  }, [startTime, endTime, dateType, rsColFilters, studyData]);

  // Download Excel
  const downloadExcel = () => {
    const headers = ["Id", "Patient", "Study", "Referral", "Status", "Center", "Assigned to", "Study Date", "Uploaded", "SignedOff", "Report", "Tag"];
    const rows = rsFilteredData.map((r) =>
      [r.id, r.patient, r.study, r.referral, r.status, r.center, r.assigned, r.studyDate, r.uploaded, r.signedOff, `Report ${r.reportCount ?? 1}`, ""].join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-summary-${dateType}-${startTime || "all"}-to-${endTime || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Pagination
  const totalRows = rsFilteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const activePage = currentPage > totalPages ? totalPages : currentPage;
  const startIndex = (activePage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const paginatedData = rsFilteredData.slice(startIndex, endIndex);

  const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, activePage - delta);
    const right = Math.min(totalPages, activePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push("..."); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push("..."); pages.push(totalPages); }
    return pages;
  };

  return (
    <>
      {editPatient && <EditModal patient={editPatient} onClose={() => setEditPatient(null)} />}
      {attachmentPatient && <AttachModal patient={attachmentPatient} onClose={() => setAttachmentPatient(null)} />}
      {historyPatient && <HistoryModel patient={historyPatient} onClose={() => setHistoryPatient(null)} />}
      {commentsPatient && <CommentsModel patient={commentsPatient} onClose={() => setCommentsPatient(null)} />}
      {userPatient && <UserModel patient={userPatient} onClose={() => setUserPatient(null)} />}
      {openPatient && <OpenModel patient={openPatient} onClose={() => setOpenPatient(null)} />}

      <div className="rs-filter-bar">
        <span className="rs-filter-label">Summary date range</span>
        <div className="rs-filter-row">
          <div className="rs-input-wrap">
            <label className="rs-input-label">Start Time</label>
            <input 
              type="date" 
              className="rs-date-input" 
              value={startTime}
              onChange={(e) => { setStartTime(e.target.value); setCurrentPage(1); }} 
            />
          </div>
          <div className="rs-input-wrap">
            <label className="rs-input-label">End Time</label>
            <input 
              type="date" 
              className="rs-date-input" 
              value={endTime}
              onChange={(e) => { setEndTime(e.target.value); setCurrentPage(1); }} 
            />
          </div>
          
          <div className="rs-input-wrap">
            <label className="rs-input-label">Date Type</label>
            <select 
              className="rs-select" 
              value={dateType}
              onChange={(e) => { setDateType(e.target.value); setCurrentPage(1); }}
            >
              {DATE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <button className="rs-download-btn" onClick={downloadExcel}>
            DOWNLOAD EXCEL
          </button>
        </div>
        <span className="rs-clear-link" onClick={clearDate}>Clear Date</span>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>Id ↓</th>
              <th>Patient</th>
              <th>Study</th>
              <th>Referral</th>
              <th>Status</th>
              <th>History</th>
              <th>Center</th>
              <th>Assigned to</th>
              <th>Study Date</th>
              <th>Uploaded</th>
              <th>SignedOff</th>
              <th>Report</th>
              <th>Tag</th>
              <th>Actions</th>
            </tr>
            <tr className="filter-row">
              <th><input className="col-filter" value={rsColFilters.id} onChange={(e) => updateRsFilter("id", e.target.value)} placeholder="ID" /></th>
              <th><input className="col-filter" value={rsColFilters.patient} onChange={(e) => updateRsFilter("patient", e.target.value)} placeholder="Patient" /></th>
              <th><input className="col-filter" value={rsColFilters.study} onChange={(e) => updateRsFilter("study", e.target.value)} placeholder="Study" /></th>
              <th><input className="col-filter" value={rsColFilters.referral} onChange={(e) => updateRsFilter("referral", e.target.value)} placeholder="Referral" /></th>
              <th><input className="col-filter" value={rsColFilters.status} onChange={(e) => updateRsFilter("status", e.target.value)} placeholder="Status" /></th>
              <th></th>
              <th><input className="col-filter" value={rsColFilters.center} onChange={(e) => updateRsFilter("center", e.target.value)} placeholder="Center" /></th>
              <th><input className="col-filter" value={rsColFilters.assigned} onChange={(e) => updateRsFilter("assigned", e.target.value)} placeholder="Assigned" /></th>
              <th>
                <div className="date-filter-wrap">
                  <input type="date" className="col-filter col-filter-date" value={rsColFilters.studyDateFrom} onChange={(e) => updateRsFilter("studyDateFrom", e.target.value)} />
                  <input type="date" className="col-filter col-filter-date" value={rsColFilters.studyDateTo} onChange={(e) => updateRsFilter("studyDateTo", e.target.value)} />
                </div>
              </th>
              <th><input type="date" className="col-filter col-filter-date" value={rsColFilters.uploadedDate} onChange={(e) => updateRsFilter("uploadedDate", e.target.value)} /></th>
              <th><input type="date" className="col-filter col-filter-date" value={rsColFilters.signedOffDate} onChange={(e) => updateRsFilter("signedOffDate", e.target.value)} /></th>
              <th></th><th></th><th></th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr><td colSpan={14} className="no-records">No records found</td></tr>
            ) : (
              paginatedData.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>
                    <Link to={`/viewer/${row.id}`} state={{
                      compareData: row,
                      patient: {
                        patient_id: row.patient_id || row.id,
                        patient_name: row.patient || row.patient_name,
                      }
                    }} className="patientLink">
                      {row.patient}
                    </Link>
                  </td>
                  <td>{row.study}</td>
                  <td>{row.referral}</td>
                  <td>
                    <span className={`status-badge status-${row.status?.replace(/\s+/g, "-").toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                  <HistoryTools
                    onEdit={() => setEditPatient(row)}
                    onAttachment={() => setAttachmentPatient(row)}
                    onHistory={() => setHistoryPatient(row)}
                    onComments={() => setCommentsPatient(row)}
                    onOpen={() => setOpenPatient(row)}
                    onUser={() => navigate(`/viewer/${row.id}`, { state: { compareData: row, openReport: true } })}
                  />
                  <td>{row.center}</td>
                  <td>
                    {row.assigned || "-"}
                    <br />

                    <button
                      className="assign-btn"
                      onClick={() => {
                        setSelectedPatient(row);
                        setAssignModal(true);
                      }}
                    >
                      Assign To
                    </button>
                  </td>
                  <td>{row.studyDate}</td>
                  <td>{row.uploaded}</td>
                  <td>{row.signedOff}</td>
                  <td className="report-cell">
                    <span className="report-link" style={{ cursor: "pointer" }}>
                      Report {row.reportCount ?? 1}
                    </span>
                  </td>
                  <td className="tag-cell">
                    <button className="tag-add-btn" title="Add tag">+</button>
                  </td>
                  <td className="actions-cell">
                    <button className="action-icon-btn download-btn" title="Download">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="8 17 12 21 16 17" />
                        <line x1="12" y1="12" x2="12" y2="21" />
                        <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" />
                      </svg>
                    </button>
                    <button className="action-icon-btn lock-btn" title="Lock">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}